import React from 'react';
import {
  Edit,
  TabbedForm,
  TextInput,
  SelectInput,
  required,
  useRecordContext,
} from 'react-admin';
import { useWatch } from 'react-hook-form';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { rolesFields, CallLineType, RoleType } from '../roles.model';
import { useEditWithRefresh } from '../../hooks/useMutationWithRefresh';
import { FeatureFlags } from '../../constants/featureFlags';
import RolePermissionsTab from './RolePermissionsTab.component';
import { validatePhone } from '../../utils/validation.js';

const RoleTitle = () => {
  const record = useRecordContext();
  if (!record) return null;
  return `Role ${record[rolesFields.roleName]}`;
};

const ConditionalLineTypeFields = () => {
  const roleType = useWatch({ name: rolesFields.roleType });
  
  // Only show line_type and phone_number for org_feature, custom_line, and legacy roles
  if (roleType === RoleType.OrgFeature.id || roleType === RoleType.CustomLine.id || roleType === RoleType.Legacy.id || !roleType) {
    return (
      <>
        <SelectInput
          label="Line Type"
          source={rolesFields.lineType}
          helperText="How users access this service (required for org features)"
          choices={[
            CallLineType.VideoChat,
            CallLineType.PhoneNumber,
            CallLineType.PhoneToPacifyProvider,
          ]}
          sx={{ width: 400 }}
        />
        <TextInput
          type="phone"
          label="Phone Number"
          source={rolesFields.phoneNumber}
          helperText="Required if line type is phone-number or phone-to-pacify-provider"
          validate={validatePhone(false)}
          sx={{ width: 400 }}
        />
        <TextInput
          label="Title (English)"
          source={rolesFields.titleEnglish}
          helperText="Display title shown to English-speaking patients"
          sx={{ width: 400 }}
        />
        <TextInput
          label="Title (Spanish)"
          source={rolesFields.titleSpanish}
          helperText="Display title shown to Spanish-speaking patients"
          sx={{ width: 400 }}
        />
        <TextInput
          label="Info Popup Content (English)"
          source={rolesFields.infoPopupContentEnglish}
          helperText="Description shown in the service popup for English-speaking patients"
          multiline
          sx={{ width: 400 }}
        />
        <TextInput
          label="Info Popup Content (Spanish)"
          source={rolesFields.infoPopupContentSpanish}
          helperText="Description shown in the service popup for Spanish-speaking patients"
          multiline
          sx={{ width: 400 }}
        />
        <TextInput
          label="Button Image URL"
          source={rolesFields.buttonImageURL}
          helperText="URL for the service button icon"
          sx={{ width: 400 }}
        />
        <TextInput
          label="Button Background Color"
          source={rolesFields.buttonBackgroundColor}
          helperText="Hex color for the service button background (e.g. #FFFFFF)"
          sx={{ width: 400 }}
        />
      </>
    );
  }
  
  return null;
};

const RolesEdit = () => {
  const mutationOptions = useEditWithRefresh('roles');
  const isRBACEnabled = useFeatureIsOn(FeatureFlags.ENABLE_ADMIN_PANEL_RBAC);

  return (
    <Edit
      title={<RoleTitle />}
      mutationMode="pessimistic"
      mutationOptions={mutationOptions}
    >
      <TabbedForm>
        <TabbedForm.Tab label="Role Details" path="">
          <TextInput
            source={rolesFields.roleName}
            label="Name"
            validate={required()}
            sx={{ width: 400 }}
          />
          <TextInput
            source={rolesFields.roleKey}
            label="Role Key"
            helperText="Auto-generated from name. Cannot be changed after creation."
            disabled
            sx={{ width: 400 }}
          />
          <SelectInput
            source={rolesFields.roleType}
            label="Role Type"
            helperText="Categorize legacy roles. Can only change from 'legacy' to other types (one-time)."
            choices={[
              RoleType.InternalAdmin,
              RoleType.Patient,
              RoleType.Provider,
              RoleType.OrgFeature,
              RoleType.CustomLine,
              RoleType.Legacy
            ]}
            sx={{ width: 400 }}
          />
          <ConditionalLineTypeFields />
        </TabbedForm.Tab>

        {isRBACEnabled && (
          <TabbedForm.Tab label="Permissions" path="permissions">
            <RolePermissionsTab />
          </TabbedForm.Tab>
        )}
      </TabbedForm>
    </Edit>
  );
};

export default RolesEdit;
